import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Grid,
  Box,
  Card,
  IconButton,
  Button,
  Input,
  InputAdornment,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import { GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import ConfirmDeleteDialog from "popup/ConfirmDeleteDialog";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import EncodingSettingsMenu from "../components/EncodingSettingsMenu";
import PatternSettingsMenu from "../components/PatternSettingsMenu";
import EventHistorySettingsMenu from "../components/EventHistorySettingsMenu";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import ErrorDialog from "popup/ErrorDialog";
import AddNewClosureDialog from "popup/AddNewClosureDialog";
import { set } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmationDialog from "popup/ConfirmationDialog";
const COLUMN_VISIBILITY_KEY = "columnVisibility_unrecognized";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

function ClosureRulesFilter() {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const darkMode = localStorage.getItem("darkMode") === "true";
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [languages, setLanguages] = useState([]);
  const toolbarRef = useRef(null);
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectDeleteRule, setSelectDeleteRule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddRuleDialog, setOpenAddRuleDialog] = useState(false);
  const [ruleContent, setRuleContent] = useState([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const savedColumnVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedColumnVisibility) {
      setColumnVisibilityModel(JSON.parse(savedColumnVisibility));
    } else {
      setColumnVisibilityModel({
        alarm_id: false,
      });
    }
  }, []);

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newModel));
  };

  const tlmGetClosureRule = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/close/rule/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Error during the API call: ", error);
    }
  };

  useEffect(() => {
    tlmGetClosureRule();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Error during the API call:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const tlmPostNewClosureRule = async (formData) => {
    try {
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/close/rule/new",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          body: {
            description: formData.description,
            account_number: formData.account_number,
            original_store: formData.original_store,
            original_equipment: formData.original_equipment,
            original_priority: formData.original_priority,
            alarm_text: formData.alarm_text,
            severity: formData.severity,
            position: formData.position,
          },
        },
      });
      return restOperation;
    } catch (error) {
      throw error;
    }
  };

  const tlmPostDeleteClosureRule = async (row) => {
    try {
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/close/rule/delete",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          body: {
            closure_rule_ids: [row.id],
          },
        },
      });
      return restOperation;
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const tlmPostClosureRuleChanges = async (formData) => {
    try {
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/close/rule/edit",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            closure_rule_id: formData.id,
          },
          body: {
            description: formData.description,
            account_number: formData.account_number,
            original_store: formData.original_store,
            original_equipment: formData.original_equipment,
            original_priority: formData.original_priority,
            alarm_text: formData.alarm_text,
            severity: formData.severity,
            position: formData.position,
          },
        },
      });
      return restOperation;
    } catch (error) {
      throw error;
    }
  };

  const columns = useMemo(() => {
    return [
      {
        field: "id",
        headerName: I18n.get("ID"),
        width: 100,
        editable: false,
      },
      {
        field: "description",
        headerName: I18n.get("Description"),
        width: 200,
        editable: false,
      },
      {
        field: "creator",
        headerName: I18n.get("Creator"),
        width: 150,
        editable: false,
      },
      {
        field: "last_modification",
        headerName: I18n.get("Last Modification Date"),
        width: 250,
        editable: false,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "last_modifier",
        headerName: I18n.get("Last Modifier"),
        width: 200,
        editable: false,
      },
      {
        field: "account_number",
        headerName: I18n.get("Account Number"),
        width: 160,
        editable: false,
      },
      {
        field: "original_store",
        headerName: I18n.get("Store"),
        width: 200,
        editable: false,
      },
      {
        field: "original_priority",
        headerName: I18n.get("Priority"),
        width: 100,
        editable: false,
      },
      {
        field: "original_equipment",
        headerName: I18n.get("Original Equipment"),
        width: 200,
        editable: false,
      },
      {
        field: "alarm_text",
        headerName: I18n.get("Alarm Text"),
        width: 200,
        editable: false,
      },
      {
        field: "severity",
        headerName: I18n.get("Severity"),
        width: 100,
        editable: false,
      },
      {
        field: "position",
        headerName: I18n.get("Position"),
        width: 100,
        editable: false,
      },
      {
        field: "actions",
        headerName: I18n.get("Actions"),
        resizable: false,
        width: 100,
        renderCell: (params) => {
          return (
            <>
              <Tooltip title={I18n.get("Edit")}>
                <GridActionsCellItem
                  icon={<EditIcon />}
                  label={I18n.get("Edit")}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEditClick(params.row, false);
                  }}
                />
              </Tooltip>
              <Tooltip title={I18n.get("Delete")}>
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label={I18n.get("Delete")}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteClick(params.row, false);
                  }}
                />
              </Tooltip>
            </>
          );
        },
      },
    ];
  }, [rowModesModel]);

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;

  const handleRowSelection = (ids) => {
    setSelectedRows(ids);
  };

  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const handleRowContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          }
        : null
    );
  };

  const handleAddRule = () => {
    setOpenAddRuleDialog(true);
  };

  const handleEditClick = (row) => {
    setRuleContent(row);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (row) => {
    setSelectDeleteRule([row]);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleSubmitRule = async (formData) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Adding new rule"),
      });
      setOpenAddRuleDialog(false);

      const restOperation = await tlmPostNewClosureRule(formData);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("New closure rule successfully added"),
        });
        // dispatch({
        //   type: "DATA_RECEIVED",
        //   value: state.audit.filter((row) => row.id !== formData.id),
        // });
        tlmGetClosureRule();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to add new closure rule"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to add new closure rule"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    }
  };

  const handleEditRule = async (formData) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Editing closure rule"),
      });
      setOpenEditDialog(false);

      const restOperation = await tlmPostClosureRuleChanges(formData);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Closure rule successfully edited"),
        });
        tlmGetClosureRule();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to edit closue rule"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to edit closure rule"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    }
  };

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false);
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Deleting closure rule"),
      });
      const restOperation = await tlmPostDeleteClosureRule(selectDeleteRule[0]);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Closure rule successfully deleted"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter(
            (row) => row.id !== selectDeleteRule.id
          ),
        });
        tlmGetClosureRule();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to delete closure rule"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to delete closure rule"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          {" "}
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Closure Rules Filter")}
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              onClick={() => handleAddRule()} // Open the pop-up
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth={tableParentIsMD}
            >
              {I18n.get("Add New Filter")}
            </Button>
          </Stack>
          <Box
            sx={{
              ml: 2,
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Input
              type="text"
              placeholder={I18n.get("Search")}
              onChange={searchChanged}
              fullWidth={tableParentIsMD}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        </Stack>
      </div>
    );
  };

  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  function getScreenDimension() {
    const screenHeight = window.screen.height;
    if (screenHeight >= 1440) {
      return "27inches";
    } else if (screenHeight >= 1080) {
      return "24inches";
    }
  }

  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const margin =
        browser === "Firefox" ? 200 : browser === "Chrome" ? 200 : 200;
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        margin;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 3, mt: 3, width: "400px" }}
        >
          <EncodingSettingsMenu />
          <PatternSettingsMenu />
          <EventHistorySettingsMenu />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              {" "}
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                  overflow: "auto",
                }}
              >
                {renderToolbar()}
                <DataGridPro
                  sx={{
                    "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                      minHeight: "2rem",
                    },
                    "&  .MuiDataGrid-cell ": {
                      cursor: "default",
                    },
                    borderColor: darkMode ? "#6B635A" : "",
                    "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                      borderColor: darkMode ? "#625a52" : "",
                    },
                    "& .MuiDataGrid-columnSeparator": {
                      borderColor: darkMode ? "#625a52" : "",
                    },
                    "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                    minHeight: "20vh",
                    flexFlow: 1,
                    height: "100%",
                  }}
                  localeText={
                    language === "it"
                      ? itIT.components.MuiDataGrid.defaultProps.localeText
                      : language === "ge"
                      ? deDE.components.MuiDataGrid.defaultProps.localeText
                      : ""
                  }
                  onCellDoubleClick={(params, event) => {
                    // Prevent the default double-click behavior
                    event.defaultMuiPrevented = true;
                  }}
                  hideFooterRowCount
                  editMode="row"
                  rows={filteredRows}
                  columns={columns}
                  //disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                  rowModesModel={rowModesModel}
                  onRowModesModelChange={(newModel) =>
                    setRowModesModel(newModel)
                  }
                  onRowSelectionModelChange={(newSelection) =>
                    handleRowSelection(newSelection)
                  }
                  initialState={{
                    density: "compact",
                    columns: {
                      columnVisibilityModel: {
                        tlm_asset_id: false,
                        id: false,
                      },
                    },
                    pinnedColumns: { right: ["actions"] },
                  }}
                  autoPageSize
                  apiRef={apiRef}
                  resizeThrottleMs={resizeThrottle}
                  loading={!state.fetched || resizeThrottle > 60}
                  onProcessRowUpdateError={(error) => console.log({ error })}
                  //columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={handleColumnVisibilityChange}
                  onSelectionModelChange={(newSelection) => {
                    const selectedRows = newSelection.map((id) =>
                      filteredRows.find((row) => row.tlm_asset_id === id)
                    );
                    setSelectedRows(selectedRows);
                  }}
                  slotProps={{
                    loadingOverlay: {
                      variant: "skeleton",
                      noRowsVariant: "skeleton",
                    },
                    row: {
                      onContextMenu: (e) => handleRowContextMenu(e),
                    },
                  }}
                  slots={{
                    toolbar: () => (
                      <GridToolbarContainer>
                        <GridToolbarColumnsButton />
                        <GridToolbarExport
                          printOptions={{ disableToolbarButton: true }}
                          csvOptions={{
                            fileName: "Closure Rules",
                            utf8WithBom: true,
                          }}
                        />
                        <GridToolbarFilterButton />
                      </GridToolbarContainer>
                    ),
                  }}
                />
                {openAddRuleDialog && (
                  <AddNewClosureDialog
                    isOpen={openAddRuleDialog}
                    onClose={() => setOpenAddRuleDialog(false)}
                    handleSubmitRule={handleSubmitRule}
                    type={"add"}
                  />
                )}
                {openEditDialog && (
                  <AddNewClosureDialog
                    isOpen={openEditDialog}
                    onClose={() => setOpenEditDialog(false)}
                    handleSubmitRule={handleEditRule}
                    mainFormData={ruleContent}
                    type={"edit"}
                  />
                )}
                <ConfirmDeleteDialog
                  parent={"closure_rule"}
                  open={openDeleteDialog}
                  onClose={handleCloseDeleteDialog}
                  onConfirm={handleConfirmDelete}
                />
                <ErrorDialog
                  open={errorDialogOpen}
                  onClose={() => setErrorDialogOpen(false)}
                  message={errorMessage}
                />
                <APIStatusDialog
                  open={apiStatus.open}
                  status={apiStatus.status}
                  message={apiStatus.message}
                />
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DashboardLayout>
    </>
  );
}

export default ClosureRulesFilter;
